/**
 * FikoZamanKontrol.ts
 * SPH simülasyonu için zaman çizelgesi (timeline) kontrol sistemi
 * Senaryo bazlı eleman aktivasyonu/deaktivasyonu ve parametre değişiklikleri
 */

/**
 * Zaman çizelgesinde bir olay
 */
export interface ZamanOlayi {
  /** Olayın gerçekleşeceği simülasyon zamanı (saniye) */
  zaman: number;

  /** Hedef eleman ID'si */
  elemanId: string;

  /** Yapılacak eylem */
  eylem: 'ac' | 'kapa' | 'ayarla';

  /** Ayarla eylemi için parametre adı */
  parametre?: string;

  /** Ayarla eylemi için yeni değer */
  deger?: any;
}

/**
 * Senaryo - Zaman çizelgesi olaylarının koleksiyonu
 */
export interface Senaryo {
  /** Senaryo adı */
  ad: string;

  /** Senaryo açıklaması */
  aciklama: string;

  /** Zaman çizelgesi olayları */
  olaylar: ZamanOlayi[];

  /** Toplam senaryo süresi (saniye) */
  sureSaniye: number;
}

/**
 * Zaman kontrol sistemi
 * Simülasyon zamanını yönetir ve zamanlanmış olayları tetikler
 */
export class FikoZamanKontrol {
  /** Simülasyon zamanı (saniye) */
  simZaman: number = 0;

  /** Simülasyon hız çarpanı (0.25x - 4x) */
  simHizi: number = 1.0;

  /** Simülasyon duraklatıldı mı? */
  duraklatildi: boolean = false;

  /** Aktif senaryo */
  aktifSenaryo: Senaryo | null = null;

  /** Çalıştırılmış olayların geçmişi */
  olayGecmisi: ZamanOlayi[] = [];

  /** Bekleyen olaylar (zamana göre sıralı) */
  private bekleyenOlaylar: ZamanOlayi[] = [];

  /** Olay callback fonksiyonu */
  onOlay: ((olay: ZamanOlayi) => void) | null = null;

  /** Senaryo tamamlanma callback'i */
  onSenaryoTamamlandi: (() => void) | null = null;

  constructor() {
    this.sifirla();
  }

  /**
   * Simülasyon zamanını ilerlet ve olayları kontrol et
   */
  adim(dt: number): void {
    if (this.duraklatildi) {
      return;
    }

    // Zamanı ilerlet
    this.simZaman += dt * this.simHizi;

    // Senaryo kontrolü
    if (this.aktifSenaryo) {
      // Senaryo bitti mi?
      if (this.simZaman >= this.aktifSenaryo.sureSaniye) {
        this.duraklat();
        if (this.onSenaryoTamamlandi) {
          this.onSenaryoTamamlandi();
        }
      }
    }

    // Tetiklenecek olayları kontrol et
    this.olaylariKontrolEt();
  }

  /**
   * Zamanı kontrol et ve tetiklenecek olayları çalıştır
   */
  private olaylariKontrolEt(): void {
    if (this.bekleyenOlaylar.length === 0) {
      return;
    }

    // Zamana göre tetiklenecek olayları bul
    const tetiklenecekler: ZamanOlayi[] = [];

    for (let i = 0; i < this.bekleyenOlaylar.length; i++) {
      const olay = this.bekleyenOlaylar[i];

      if (olay.zaman <= this.simZaman) {
        tetiklenecekler.push(olay);
      } else {
        // Liste sıralı olduğu için buradan sonrasını kontrol etmeye gerek yok
        break;
      }
    }

    // Tetiklenen olayları çalıştır
    for (const olay of tetiklenecekler) {
      this.olayTetikle(olay);

      // Bekleyen listeden çıkar
      const index = this.bekleyenOlaylar.indexOf(olay);
      if (index !== -1) {
        this.bekleyenOlaylar.splice(index, 1);
      }

      // Geçmişe ekle
      this.olayGecmisi.push(olay);
    }
  }

  /**
   * Bir olayı tetikle
   */
  private olayTetikle(olay: ZamanOlayi): void {
    if (this.onOlay) {
      this.onOlay(olay);
    }
  }

  /**
   * Simülasyonu başlat
   */
  baslat(): void {
    this.duraklatildi = false;
  }

  /**
   * Simülasyonu duraklat
   */
  duraklat(): void {
    this.duraklatildi = true;
  }

  /**
   * Simülasyonu sıfırla
   */
  sifirla(): void {
    this.simZaman = 0;
    this.duraklatildi = true;
    this.olayGecmisi = [];
    this.bekleyenOlaylar = [];

    // Aktif senaryoyu yeniden yükle
    if (this.aktifSenaryo) {
      this.senaryoYukle(this.aktifSenaryo);
    }
  }

  /**
   * Senaryo yükle
   */
  senaryoYukle(senaryo: Senaryo): void {
    this.aktifSenaryo = senaryo;
    this.simZaman = 0;
    this.olayGecmisi = [];

    // Olayları zamana göre sırala ve bekleyen listeye ekle
    this.bekleyenOlaylar = [...senaryo.olaylar].sort((a, b) => a.zaman - b.zaman);

    this.duraklatildi = true;
  }

  /**
   * Mevcut durumu senaryo olarak kaydet
   */
  senaryoKaydet(): Senaryo {
    const senaryo: Senaryo = {
      ad: 'Yeni Senaryo',
      aciklama: 'Kullanıcı tarafından oluşturuldu',
      olaylar: [...this.olayGecmisi, ...this.bekleyenOlaylar].sort((a, b) => a.zaman - b.zaman),
      sureSaniye: this.aktifSenaryo?.sureSaniye || 30.0
    };

    return senaryo;
  }

  /**
   * Zaman çizelgesine olay ekle
   */
  olayEkle(olay: ZamanOlayi): void {
    // Zamana göre sıralı ekle
    let eklendi = false;

    for (let i = 0; i < this.bekleyenOlaylar.length; i++) {
      if (this.bekleyenOlaylar[i].zaman > olay.zaman) {
        this.bekleyenOlaylar.splice(i, 0, olay);
        eklendi = true;
        break;
      }
    }

    if (!eklendi) {
      this.bekleyenOlaylar.push(olay);
    }

    // Aktif senaryoyu güncelle
    if (this.aktifSenaryo) {
      this.aktifSenaryo.olaylar.push(olay);
    }
  }

  /**
   * Zaman çizelgesinden olay sil
   */
  olaySil(index: number): void {
    if (index >= 0 && index < this.bekleyenOlaylar.length) {
      const olay = this.bekleyenOlaylar[index];
      this.bekleyenOlaylar.splice(index, 1);

      // Aktif senaryodan da sil
      if (this.aktifSenaryo) {
        const senaryoIndex = this.aktifSenaryo.olaylar.indexOf(olay);
        if (senaryoIndex !== -1) {
          this.aktifSenaryo.olaylar.splice(senaryoIndex, 1);
        }
      }
    }
  }

  /**
   * Hız ayarla (0.25x - 4x)
   */
  hiziAyarla(hiz: number): void {
    this.simHizi = Math.max(0.25, Math.min(4.0, hiz));
  }

  /**
   * JSON serileştirme
   */
  toJSON(): any {
    return {
      simZaman: this.simZaman,
      simHizi: this.simHizi,
      duraklatildi: this.duraklatildi,
      aktifSenaryo: this.aktifSenaryo,
      olayGecmisi: this.olayGecmisi
    };
  }

  /**
   * JSON'dan yükle
   */
  static fromJSON(data: any): FikoZamanKontrol {
    const kontrol = new FikoZamanKontrol();

    kontrol.simZaman = data.simZaman || 0;
    kontrol.simHizi = data.simHizi || 1.0;
    kontrol.duraklatildi = data.duraklatildi ?? true;
    kontrol.olayGecmisi = data.olayGecmisi || [];

    if (data.aktifSenaryo) {
      kontrol.senaryoYukle(data.aktifSenaryo);
    }

    return kontrol;
  }
}

/**
 * Hazır senaryolar
 */
export const HAZIR_SENARYOLAR: Senaryo[] = [
  {
    ad: 'Altan Döküm',
    aciklama: 'Klasik altan döküm simülasyonu - tek giriş, sabit akış',
    sureSaniye: 20.0,
    olaylar: [
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ac'
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'pozisyon',
        deger: { x: 0, y: -0.5, z: 0 }
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'yon',
        deger: { x: 0, y: 1, z: 0 }
      },
      {
        zaman: 15.0,
        elemanId: 'giris_1',
        eylem: 'kapa'
      }
    ]
  },
  {
    ad: 'Üstten Döküm',
    aciklama: 'Üstten döküm simülasyonu - rampa ile yumuşak başlangıç',
    sureSaniye: 25.0,
    olaylar: [
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ac'
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'pozisyon',
        deger: { x: 0, y: 1.5, z: 0 }
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'yon',
        deger: { x: 0, y: -1, z: 0 }
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'akisProfili',
        deger: 1 // AkisProfili.RAMPA
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'rampaYukselis',
        deger: 2.0
      },
      {
        zaman: 20.0,
        elemanId: 'giris_1',
        eylem: 'kapa'
      }
    ]
  },
  {
    ad: 'Çoklu Giriş Döküm',
    aciklama: 'Sıralı çoklu giriş aktivasyonu',
    sureSaniye: 30.0,
    olaylar: [
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ac'
      },
      {
        zaman: 5.0,
        elemanId: 'giris_2',
        eylem: 'ac'
      },
      {
        zaman: 10.0,
        elemanId: 'giris_3',
        eylem: 'ac'
      },
      {
        zaman: 20.0,
        elemanId: 'giris_1',
        eylem: 'kapa'
      },
      {
        zaman: 22.0,
        elemanId: 'giris_2',
        eylem: 'kapa'
      },
      {
        zaman: 25.0,
        elemanId: 'giris_3',
        eylem: 'kapa'
      }
    ]
  },
  {
    ad: 'Pulse Döküm',
    aciklama: 'Pulse modlu döküm (PWM kontrolü)',
    sureSaniye: 30.0,
    olaylar: [
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ac'
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'akisProfili',
        deger: 3 // AkisProfili.PULSE
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'pulsePeriyot',
        deger: 2.0
      },
      {
        zaman: 0.0,
        elemanId: 'giris_1',
        eylem: 'ayarla',
        parametre: 'pulseDuty',
        deger: 0.6
      },
      {
        zaman: 25.0,
        elemanId: 'giris_1',
        eylem: 'kapa'
      }
    ]
  }
];
